import { FC, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { createGlobalStyle, useTheme } from 'styled-components';
import {
  FpComponentsLibraryHelmet,
  FpComponentsLibraryProvider,
  getStyledThemeColor,
  LANGUAGE,
  Page403,
  Page404,
  PrimaryTheme
} from '@flicent/fieldpulse-react-component-library-frontend';

import { IS_BUILD_FOR_OFFLINE, IS_PROD, ROUTE } from '@/constants';
import { CustomFormAnalyticsInternalContextProvider, CustomFormAnalyticsPublicLinkContextProvider } from '@/contexts';
import { AllFormsPage, CustomFormPageComponent, CustomFormTemplateEditorPage, PublicLinkPage } from '@/pages';
import { loadAppLocale } from './i18n';


const GlobalStyle = createGlobalStyle`
  html {
    background-color: ${ getStyledThemeColor('veryLightGrey') };
  }

  body {
    margin: 0;
    padding: 0;
    touch-action: pan-x pan-y; // Prevent zoom on IOS
  }
`;

export const BaseCustomFormPageGlobalStyle: FC = () => {
  const theme = useTheme();
  return <GlobalStyle theme={ theme }/>;
};

const App = () => {
  const language = LANGUAGE.en;

  const navigate = useNavigate();
  const location = useLocation();
  const [pageTitle, setPageTitle] = useState<string>('FieldPulse');

  const homePagePath: string = '/' + location.search + location.hash;
  const isHome: boolean = location.pathname === '/' || !location.pathname;

  // Load the library locale when the language changes
  useEffect(() => {
    loadAppLocale(language);
  }, [language]);

  return (
    <FpComponentsLibraryProvider isOffline={ IS_BUILD_FOR_OFFLINE } language={ language } fpTheme={ PrimaryTheme }>
      <FpComponentsLibraryHelmet>
        <title>{ pageTitle }</title>
      </FpComponentsLibraryHelmet>
      <BaseCustomFormPageGlobalStyle/>
      {
        !IS_PROD && !isHome && (
          <button
            title="Go to Home Page"
            onClick={ () => navigate(homePagePath) }
            style={ {
              position: 'fixed',
              top: 0,
              left: 5,
              padding: '2px 4px',
              borderBottomLeftRadius: 5,
              borderBottomRightRadius: 5,
              border: '1px solid lightgray',
              fontSize: '11px',
              lineHeight: 1,
              backgroundColor: 'white',
              color: 'gray',
              cursor: 'w-resize'
            } }
          >
            <img src="/icons/apple-icon.png" alt="Home" width={ 12 }/>
            Go to All Forms
          </button>
        )
      }

      <Routes>
        { !IS_PROD && <Route index={ true } path="/" element={ <AllFormsPage/> }/> }

        <Route path={ ROUTE.customFormTemplate }
               element={ <CustomFormTemplateEditorPage onChangePageTitle={ setPageTitle }/> }/>

        <Route path={ ROUTE.customForm } element={
          <CustomFormAnalyticsInternalContextProvider>
            <CustomFormPageComponent onChangePageTitle={ setPageTitle }/>
          </CustomFormAnalyticsInternalContextProvider>
        }/>

        <Route path={ ROUTE.public } element={
          <CustomFormAnalyticsPublicLinkContextProvider>
            <PublicLinkPage onChangePageTitle={ setPageTitle }/>
          </CustomFormAnalyticsPublicLinkContextProvider>
        }/>

        <Route path={ ROUTE.forbidden } element={ <Page403/> }/>
        <Route path={ ROUTE.notFound } element={ <Page404/> }/>

        <Route path="*" element={ <Navigate to={ ROUTE.notFound }/> }/>
      </Routes>
    </FpComponentsLibraryProvider>
  );
};

export default App;